// src/pages/orderDelivery/OrdersDelivered.jsx

import { useState, useEffect, useMemo, useCallback } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../services/firebase";
import { useBuyer } from "../../contexts/BuyerContext";
import Header from "../../components/Header";
import PayModal from "./PayModal";
import ReturnModal from "./ReturnModal";
import { ChevronDown, ChevronUp, RotateCcw, CreditCard } from "lucide-react";
import { num } from "./utils";
import "./OrdersDelivered.css";
import "../OrdersinProgress.css";

const OrdersDelivered = () => {
  const { buyer } = useBuyer();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSeller, setExpandedSeller] = useState(null);
  const [payModal, setPayModal] = useState(null);
  const [returnModal, setReturnModal] = useState(null);

  const config = useMemo(
    () => ({
      statuses: ["Delivered"],
      title: "Delivered",
    }),
    [],
  );

  // ─────────────────────────────────────────────────────────────
  // ORDER METRICS
  // ─────────────────────────────────────────────────────────────
  const enrichOrderWithMetrics = useCallback((order) => {
    const orderDetailEntries = Object.values(order.orderDetail || {});
    const hasDetail = orderDetailEntries.length > 0;

    // ───────────────────────────────────────────────────────────
    // 1. WASTAGE
    // ───────────────────────────────────────────────────────────
    const baseWastage =
      num(order.wastage) +
      num(order.wastageOperator) +
      num(order.wastageJmi) +
      num(order.specificationMC);

    const hasSpecification =
      order.specification &&
      order.specification.toString().trim().toUpperCase() !== "PLANE";

    const specAdditions = hasSpecification
      ? (num(order.specificationMC) * num(order.specificationMakingJmi)) / 100 +
        (num(order.specificationMC) * num(order.specificationMakingOperator)) /
          100
      : 0;

    const totalWastage =
      num(order.buyerDeskCalculation) + baseWastage + specAdditions;

    // ───────────────────────────────────────────────────────────
    // 2. MAKING CHARGES
    // ───────────────────────────────────────────────────────────
    const setMcBase = num(order.setMc || order.lotDetails?.[0]?.setMc);

    const setMc =
      setMcBase +
      setMcBase *
        ((num(order.setMakingJmi) + num(order.setMakingOperator)) / 100);

    const gramMcBase = num(order.netGramMc || order.lotDetails?.[0]?.netGramMc);

    const gramMcPerGram =
      gramMcBase +
      gramMcBase *
        ((num(order.gramMakingJmi) + num(order.gramMakingOperator)) / 100);

    const specMcBase = num(
      order.specificationGramRate ||
        order.lotDetails?.[0]?.specificationGramRate,
    );

    const specMcPerGram =
      specMcBase +
      specMcBase *
        ((num(order.specificationGramRateJmi) +
          num(order.specificationGramRateOperator)) /
          100);

    // ───────────────────────────────────────────────────────────
    // 3. WEIGHT / QUANTITY
    // ───────────────────────────────────────────────────────────
    let displayGrossWt = 0;
    let displayNetWt = 0;
    let displaySpecWt = 0;
    let displayQty = 1;
    let stampCertCost = 0;

    if (hasDetail) {
      // FINAL SCENARIO
      const totalFinalQty = orderDetailEntries.reduce((sum, d) => {
        const fQty =
          d.finalQty !== undefined
            ? num(d.finalQty)
            : num(d.qty) - num(d.returnQty);

        return sum + Math.max(0, fQty);
      }, 0);

      const totalDetailFinalGross = orderDetailEntries.reduce((sum, d) => {
        const fGross =
          d.finalGrossWt !== undefined
            ? num(d.finalGrossWt)
            : num(d.qty) * num(d.grossWT) - num(d.returnGrossWt);

        return sum + Math.max(0, fGross);
      }, 0);

      const totalDetailFinalNet = orderDetailEntries.reduce((sum, d) => {
        const fNet =
          d.finalNetWt !== undefined
            ? num(d.finalNetWt)
            : num(d.qty) * num(d.netwt) - num(d.returnNetWt);

        return sum + Math.max(0, fNet);
      }, 0);

      const totalDetailFinalSpec = orderDetailEntries.reduce((sum, d) => {
        const fSpec =
          d.finalSpecWt !== undefined
            ? num(d.finalSpecWt)
            : num(d.qty) * num(d.specWT) - num(d.returnSpecWt);

        return sum + Math.max(0, fSpec);
      }, 0);

      // ─────────────────────────────────────────────────────────
      // STAMP + CERTIFICATE COST
      // ─────────────────────────────────────────────────────────
      stampCertCost = orderDetailEntries.reduce((sum, d) => {
        const qty =
          d.finalQty !== undefined
            ? num(d.finalQty)
            : num(d.qty) - num(d.returnQty);

        return (
          sum +
          num(d.stampCertificateQtyPerSet) *
            Math.max(0, qty) *
            num(order.stampCertificateCost)
        );
      }, 0);

      displayQty = totalFinalQty > 0 ? totalFinalQty : num(order.quantity) || 1;

      displayGrossWt = parseFloat(totalDetailFinalGross.toFixed(3));

      displayNetWt = parseFloat(totalDetailFinalNet.toFixed(3));

      displaySpecWt = parseFloat(totalDetailFinalSpec.toFixed(3));
    } else {
      // APPROX SCENARIO
      let lotNet = 0;
      let lotGross = 0;
      let lotSpec = 0;
      let totalSet = 0;

      if (
        Array.isArray(order.selectedVariants) &&
        order.selectedVariants.length > 0
      ) {
        order.selectedVariants.forEach((variant) => {
          const lot = (order.lotDetails || []).find(
            (l) => l.size === variant.size,
          );

          const qty = num(variant?.quantity) || 1;

          const avgNet = lot
            ? num(lot.avgNetWt)
            : num(order.netWt) / (num(order.quantity) || 1);

          const avgGross = lot
            ? num(lot.avgGrossWt)
            : num(order.grossWt) / (num(order.quantity) || 1);

          const avgSpec = lot
            ? num(lot.avgSpecWt)
            : num(order.specificationWt) / (num(order.quantity) || 1);

          totalSet += qty;
          lotNet += avgNet * qty;
          lotGross += avgGross * qty;
          lotSpec += avgSpec * qty;
        });
      } else if (
        Array.isArray(order.lotDetails) &&
        order.lotDetails.length > 0
      ) {
        order.lotDetails.forEach((lot) => {
          const qty =
            num(lot.quantity) || num(lot.set) || num(order.quantity) || 1;

          totalSet += qty;

          lotNet += num(lot.avgNetWt || num(lot.netWt) / qty) * qty;

          lotGross += num(lot.avgGrossWt || num(lot.grossWt) / qty) * qty;

          lotSpec += num(lot.avgSpecWt || num(lot.specWt) / qty) * qty;
        });
      }

      displayQty = totalSet > 0 ? totalSet : num(order.quantity) || 1;

      displayGrossWt = parseFloat(
        (lotGross > 0 ? lotGross : num(order.grossWt)).toFixed(3),
      );

      displayNetWt = parseFloat(
        (lotNet > 0 ? lotNet : num(order.netWt)).toFixed(3),
      );

      displaySpecWt = parseFloat(
        (lotSpec > 0 ? lotSpec : num(order.specificationWt)).toFixed(3),
      );

      stampCertCost =
        num(order.stampCertificateQtyPerSet) *
        displayQty *
        num(order.stampCertificateCost);
    }

    // ───────────────────────────────────────────────────────────
    // 4. FINE WEIGHT
    // ───────────────────────────────────────────────────────────
    const displayFineWt = parseFloat(
      ((displayNetWt * totalWastage) / 100).toFixed(3),
    );

    // ───────────────────────────────────────────────────────────
    // 5. MAKING CHARGES
    // ───────────────────────────────────────────────────────────
    const displayMc = parseFloat(
      (
        setMc * displayQty +
        gramMcPerGram * displayNetWt +
        specMcPerGram * displaySpecWt
      ).toFixed(2),
    );

    // ───────────────────────────────────────────────────────────
    // 6. TOTAL AMOUNT
    //
    // Total Amount = Total MC + Stamp & Certificate
    // ───────────────────────────────────────────────────────────
    const totalAmount = parseFloat((displayMc + stampCertCost).toFixed(2));

    return {
      ...order,

      displayGrossWt,
      displayNetWt,
      displaySpecWt,
      displayFineWt,
      displayMc,

      stampCertCost: parseFloat(stampCertCost.toFixed(2)),

      totalAmount,

      totalWastage,
      hasDetail,
      isApprox: !hasDetail,
      displayQty,
    };
  }, []);

  // ─────────────────────────────────────────────────────────────
  // GROUP ORDERS
  // ─────────────────────────────────────────────────────────────
  const ordersBySellerAndStatus = useMemo(() => {
    const groupMap = new Map();

    orders.forEach((order) => {
      const opId = order.operatorId || order.sellerId || "Unknown Operator";

      const sellerName = order.sellerId || order.operatorId || "Unknown Seller";

      const orderStatus = order.orderStatus || "Unknown Status";

      const groupKey = `${opId}||${orderStatus}`;

      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, {
          sellerName,
          operatorId: order.operatorId || order.sellerId || null,
          orderStatus,
          orders: [],
          totalItems: 0,
          totalFineWeight: 0,
          totalMc: 0,

          // NEW
          totalStampCertCost: 0,
          totalAmount: 0,
        });
      }

      const group = groupMap.get(groupKey);

      if (!group.operatorId && (order.operatorId || order.sellerId)) {
        group.operatorId = order.operatorId || order.sellerId;
      }

      const enrichedOrder = enrichOrderWithMetrics(order);

      group.orders.push(enrichedOrder);

      group.totalItems += enrichedOrder.displayQty || 1;

      group.totalFineWeight += enrichedOrder.displayFineWt || 0;

      group.totalMc += enrichedOrder.displayMc || 0;

      // NEW: accumulate stamp/certificate separately
      group.totalStampCertCost += enrichedOrder.stampCertCost || 0;

      // NEW: MC + Stamp & Certificate
      group.totalAmount += enrichedOrder.totalAmount || 0;
    });

    // Round group totals
    groupMap.forEach((group) => {
      group.totalFineWeight = parseFloat(group.totalFineWeight.toFixed(3));

      group.totalMc = parseFloat(group.totalMc.toFixed(2));

      group.totalStampCertCost = parseFloat(
        group.totalStampCertCost.toFixed(2),
      );

      group.totalAmount = parseFloat(group.totalAmount.toFixed(2));
    });

    return Array.from(groupMap.values());
  }, [orders, enrichOrderWithMetrics]);

  // ─────────────────────────────────────────────────────────────
  // FETCH ORDERS
  // ─────────────────────────────────────────────────────────────
  const fetchOrders = useCallback(async () => {
    const buyerId = buyer?.buyerId;

    if (!buyerId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const q = query(
        collection(db, "orderList"),
        where("buyerId", "==", buyerId),
        where("orderStatus", "in", config.statuses),
      );

      const snapshot = await getDocs(q);

      const fetched = snapshot.docs.map((doc) => ({
        ...doc.data(),
        docId: doc.id,
        _docId: doc.id,
        id: doc.id,
      }));

      setOrders(fetched);
    } catch (err) {
      setError(`Failed to load orders: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [buyer?.buyerId, config.statuses]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // ─────────────────────────────────────────────────────────────
  // TOGGLE SELLER
  // ─────────────────────────────────────────────────────────────
  const toggleSeller = useCallback((key) => {
    setExpandedSeller((cur) => (cur === key ? null : key));
  }, []);

  // ─────────────────────────────────────────────────────────────
  // RETURN CONFIRM
  // ─────────────────────────────────────────────────────────────
  const handleReturnConfirm = useCallback((groupKey, returnItems) => {
    setOrders((prevOrders) =>
      prevOrders.map((order) => {
        const returnItem = returnItems.find(
          (item) => item.orderId === order.id,
        );

        if (!returnItem || !returnItem.orderDetail) {
          return order;
        }

        return {
          ...order,
          orderDetail: {
            ...order.orderDetail,
            ...returnItem.orderDetail,
          },
        };
      }),
    );
  }, []);

  // ─────────────────────────────────────────────────────────────
  // FORMAT DATE
  // ─────────────────────────────────────────────────────────────
  const formatDate = useCallback((dateValue) => {
    if (!dateValue) return "N/A";

    const date =
      typeof dateValue === "object" && dateValue.seconds
        ? new Date(dateValue.seconds * 1000)
        : new Date(dateValue);

    return isNaN(date.getTime())
      ? "N/A"
      : date.toLocaleDateString("en-US", {
          day: "numeric",
          month: "short",
        });
  }, []);

  // ─────────────────────────────────────────────────────────────
  // LOADING
  // ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <>
        <Header title={config.title} />

        <div className="orders-container">
          <div className="loading-state">
            <p>Loading orders...</p>
          </div>
        </div>
      </>
    );
  }

  // ─────────────────────────────────────────────────────────────
  // ERROR
  // ─────────────────────────────────────────────────────────────
  if (error) {
    return (
      <>
        <Header title={config.title} />

        <div className="orders-container">
          <div className="error-state">
            <p className="error">{error}</p>

            <button
              onClick={() => window.location.reload()}
              className="retry-btn"
            >
              Try Again
            </button>
          </div>
        </div>
      </>
    );
  }

  // ─────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────
  return (
    <>
      <Header title={config.title} />

      <div className="orders-container">
        <div className="orders-header">
          <p>
            {orders.length} order
            {orders.length !== 1 ? "s" : ""} found
          </p>
        </div>

        {orders.length === 0 ? (
          <div className="empty-state">
            <p>No delivered orders found.</p>
          </div>
        ) : (
          <div className="seller-orders-container">
            {ordersBySellerAndStatus.map((sellerGroup) => {
              const groupKey = `${sellerGroup.sellerName}||${sellerGroup.orderStatus}`;

              const isExpanded = expandedSeller === groupKey;

              const firstOrder = sellerGroup.orders[0];

              const enrichedOrders = sellerGroup.orders;

              const displayFineWeight = parseFloat(
                sellerGroup.totalFineWeight.toFixed(3),
              );

              const displayTotalMc = parseFloat(sellerGroup.totalMc.toFixed(2));

              const displayTotalStamp = parseFloat(
                sellerGroup.totalStampCertCost.toFixed(2),
              );
              // NEW:
              // MC + Stamp & Certificate
              const displayTotalAmount = parseFloat(
                (displayTotalMc + displayTotalStamp).toFixed(2),
              );
              const displayTotalItems = sellerGroup.totalItems;
              const enrichedGroup = {
                ...sellerGroup,
                orders: enrichedOrders,
                displayFineWeight,
                displayTotalMc: displayTotalMc,
                displayTotalAmount: displayTotalAmount,
                displayTotalStampCertCost: displayTotalStamp,
              };

              return (
                <div key={groupKey} className="seller-order-card">
                  <div
                    className="seller-header clickable"
                    onClick={() => toggleSeller(groupKey)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) =>
                      e.key === "Enter" && toggleSeller(groupKey)
                    }
                  >
                    <div className="seller-info-row">
                      <div className="seller-meta">
                        <div className="order-meta-chips">
                          <span className="order-chip count-chip">
                            {displayTotalItems} item
                            {displayTotalItems !== 1 ? "s" : ""}
                          </span>

                          <span className="order-chip date-chip">
                            Delivered {formatDate(firstOrder?.orderTime)}
                          </span>
                        </div>
                      </div>

                      <span className="accordbtn" aria-label="Toggle details">
                        {isExpanded ? (
                          <ChevronUp size={20} />
                        ) : (
                          <ChevronDown size={20} />
                        )}
                      </span>
                    </div>

                    {/* ───────────────────────────
                          TOP LEVEL SUMMARY
                      ─────────────────────────── */}
                    <div className="order-summary-pills">
                      <div className="summary-pill fine-pill">
                        <span className="pill-label">Total Fine Wt</span>

                        <span className="pill-value">
                          {displayFineWeight.toFixed(3)}g
                        </span>
                      </div>

                      <div className="summary-pill mc-pill">
                        <span className="pill-label">Total Amount</span>

                        <span className="pill-value">
                          ₹{displayTotalAmount.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="od-action-buttons">
                    <button
                      className="od-btn-pay"
                      onClick={() => setPayModal(enrichedGroup)}
                    >
                      <CreditCard size={16} />
                      <span>Pay Now</span>
                    </button>

                    <button
                      className="od-btn-return-outline"
                      onClick={() =>
                        setReturnModal({
                          group: sellerGroup,
                          groupKey,
                        })
                      }
                    >
                      <RotateCcw size={16} />
                      <span>Return</span>
                    </button>
                  </div>

                  {/* ─────────────────────────────
                        EXPANDED ITEMS
                    ───────────────────────────── */}
                  {isExpanded && (
                    <>
                      <div className="divider" />

                      <div className="order-items">
                        {sellerGroup.orders.map((order) => {
                          console.log(order)
                          const imageUrl = order.images?.[0]?.url?.trim();
                          const displayQty = order.displayQty;
                          const displayGrossWt = order.displayGrossWt;
                          const displayNetWt = order.displayNetWt;
                          const displaySpecWt = order.displaySpecWt;
                          const displayFineWt = order.displayFineWt;
                          const displayMc = order.displayMc;
                          const stampCost = order.stampCertCost || 0;
                          return (
                            <div key={order.id} className="order-item">
                              <div className="item-top-row">
                                <div className="item-image">
                                  {imageUrl ? (
                                    <img
                                      src={imageUrl}
                                      alt={order.productName || "Product"}
                                      loading="lazy"
                                    />
                                  ) : (
                                    <div className="image-placeholder">
                                      No Image
                                    </div>
                                  )}
                                </div>

                                <div className="item-header-row">
                                  <div className="item-title-row">
                                    <h4 className="item-name">
                                      {order.productName || "Unnamed Product"}
                                    </h4>

                                    <span
                                      className={`item-status status-${
                                        order.orderStatus?.toLowerCase() ||
                                        "unknown"
                                      }`}
                                    >
                                      {order.orderStatus || "Delivered"}
                                    </span>
                                  </div>

                                  <div className="item-specs">
                                    <span className="spec-badge purity-badge">
                                      {order.purity || "916"} HUID
                                    </span>

                                    <span className="spec-badge">
                                      Qty: {displayQty}
                                    </span>

                                    <span className="spec-badge">
                                      Gross: {displayGrossWt.toFixed(3)}g
                                    </span>

                                    <span className="spec-badge">
                                      Net: {displayNetWt.toFixed(3)}g
                                    </span>

                                    {displaySpecWt > 0 && (
                                      <span className="spec-badge spec-wt-badge">
                                        Spec: {displaySpecWt.toFixed(3)}g
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div className="item-details">
                                <div className="item-metrics-grid">
                                  <div className="metric-cell">
                                    <span className="metric-label">
                                      Fine Wt
                                    </span>

                                    <strong className="metric-val fine-highlight">
                                      {displayFineWt.toFixed(3)}g
                                    </strong>
                                  </div>

                                  <div className="metric-cell">
                                    <span className="metric-label">
                                      Gross Wt
                                    </span>

                                    <strong className="metric-val">
                                      {displayGrossWt.toFixed(3)}g
                                    </strong>
                                  </div>

                                  <div className="metric-cell">
                                    <span className="metric-label">Net Wt</span>

                                    <strong className="metric-val">
                                      {displayNetWt.toFixed(3)}g
                                    </strong>
                                  </div>

                                  <div className="metric-cell">
                                    <span className="metric-label">
                                      Total MC
                                    </span>

                                    <strong className="metric-val mc-highlight">
                                      ₹{displayMc.toFixed(2)}
                                    </strong>
                                  </div>

                                  {stampCost > 0 && (
                                    <div className="metric-cell stamp-cell">
                                      <span className="metric-label">
                                        Stamp & Cert
                                      </span>

                                      <strong className="metric-val stamp-highlight">
                                        ₹{stampCost.toFixed(2)}
                                      </strong>
                                    </div>
                                  )}

                                  {/* NEW ITEM TOTAL */}
                                  <div className="metric-cell">
                                    <span className="metric-label">
                                      Total Amount
                                    </span>

                                    <strong className="metric-val">
                                      ₹{(displayMc + stampCost).toFixed(2)}
                                    </strong>
                                  </div>
                                </div>

                                {/* ───────────────────
                                        SIZE-WISE BREAKDOWN
                                    ─────────────────── */}
                                {order.orderDetail &&
                                  Object.keys(order.orderDetail).length > 0 && (
                                    <div className="od-order-detail-section">
                                      <h5 className="od-order-detail-title">
                                        Size Wise Breakdown
                                      </h5>

                                      <div className="od-order-detail-cards">
                                        {Object.entries(order.orderDetail).map(
                                          ([sizeName, detail]) => {
                                            const stampQty = num(
                                              detail.stampCertificateQtyPerSet,
                                            );

                                            const sizeFinalQty =
                                              detail.finalQty !== undefined
                                                ? num(detail.finalQty)
                                                : num(detail.qty) -
                                                  num(detail.returnQty);

                                            return (
                                              <div
                                                key={sizeName}
                                                className="od-size-detail-card"
                                              >
                                                <div className="od-size-detail-header">
                                                  <span className="od-size-name">
                                                    {sizeName}
                                                  </span>

                                                  <span className="od-size-qty">
                                                    Qty: {detail.qty}
                                                  </span>
                                                </div>

                                                <div className="od-size-detail-grid">
                                                  <span>
                                                    Gross:{" "}
                                                    <strong>
                                                      {num(
                                                        detail.grossWT,
                                                      ).toFixed(3)}
                                                      g
                                                    </strong>
                                                  </span>

                                                  <span>
                                                    Net:{" "}
                                                    <strong>
                                                      {num(
                                                        detail.netwt,
                                                      ).toFixed(3)}
                                                      g
                                                    </strong>
                                                  </span>

                                                  {num(detail.specWT) > 0 && (
                                                    <span>
                                                      Spec:{" "}
                                                      <strong>
                                                        {num(
                                                          detail.specWT,
                                                        ).toFixed(3)}
                                                        g
                                                      </strong>
                                                    </span>
                                                  )}

                                                  {stampQty > 0 && (
                                                    <span className="stamp-detail-tag">
                                                      Stamp:{" "}
                                                      <strong>
                                                        {stampQty}
                                                        /set
                                                      </strong>
                                                    </span>
                                                  )}
                                                </div>

                                                {num(detail.returnQty) > 0 && (
                                                  <div className="od-size-return-info">
                                                    <div className="od-return-badge">
                                                      Return: {detail.returnQty}{" "}
                                                      pcs ·{" "}
                                                      {num(
                                                        detail.returnGrossWt,
                                                      ).toFixed(3)}
                                                      g gross ·{" "}
                                                      {num(
                                                        detail.returnNetWt,
                                                      ).toFixed(3)}
                                                      g net
                                                    </div>

                                                    {detail.returnReason && (
                                                      <div className="od-return-reason-text">
                                                        Reason:{" "}
                                                        <span>
                                                          {detail.returnReason}
                                                        </span>
                                                      </div>
                                                    )}

                                                    <div className="od-final-badge">
                                                      Final: {sizeFinalQty} pcs
                                                      ·{" "}
                                                      {num(
                                                        detail.finalGrossWt,
                                                      ).toFixed(3)}
                                                      g gross ·{" "}
                                                      {num(
                                                        detail.finalNetWt,
                                                      ).toFixed(3)}
                                                      g net
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          },
                                        )}
                                      </div>
                                    </div>
                                  )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ─────────────────────────────────────────────────────────
          PAY MODAL
      ───────────────────────────────────────────────────────── */}
      {payModal && (
        <PayModal
          group={payModal}
          onClose={() => {
            setPayModal(null);
            fetchOrders();
          }}
        />
      )}

      {/* ─────────────────────────────────────────────────────────
          RETURN MODAL
      ───────────────────────────────────────────────────────── */}
      {returnModal && (
        <ReturnModal
          group={returnModal.group}
          groupKey={returnModal.groupKey}
          onReturn={handleReturnConfirm}
          onClose={() => setReturnModal(null)}
        />
      )}
    </>
  );
};

export default OrdersDelivered;
